import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { getEnv } from '~/env';
import JOCClubMembershipABI from '~/constants/joc-club-membership-abi.json';

const CONTRACT_ADDRESS = getEnv('NEXT_PUBLIC_JOC_CLUB_CONTRACT_ADDRESS') || '';
const RPC_URL = getEnv('NEXT_PUBLIC_JOC_URL') || 'http://localhost:8546';
const CHAIN_ID = Number(getEnv('NEXT_PUBLIC_JOC_CHAIN_ID') || '81');

export interface MemberInfo {
  lockedAmount: bigint;
  tier: number; // 0=None, 1=White, 2=Bronze, 3=Silver, 4=Gold, 5=Platinum
  joinedAt: number;
}

const TIER_NAMES = ['None', 'White', 'Bronze', 'Silver', 'Gold', 'Platinum'];

export type JocClubErrorCode =
  | 'ALREADY_MEMBER'
  | 'NOT_MEMBER'
  | 'INSUFFICIENT_AMOUNT'
  | 'SOULBOUND_TRANSFER'
  | 'REJECTED'
  | 'UNKNOWN';

// Thrown by join/addStake/leave so callers can map `.code` to a translated,
// user-facing message instead of showing ethers' raw revert/RPC error text.
export class JocClubActionError extends Error {
  code: JocClubErrorCode;
  constructor(code: JocClubErrorCode, message: string) {
    super(message);
    this.name = 'JocClubActionError';
    this.code = code;
  }
}

// ethers v6 decodes a revert's custom error name onto `error.revert.name` when
// the ABI includes the error fragment and the RPC call returns revert data
// (an `eth_call`/staticCall does; Hardhat's `eth_estimateGas` sometimes doesn't,
// which is why we pre-flight with staticCall before ever sending a transaction).
function decodeRevertName(error: any): string | null {
  return error?.revert?.name ?? null;
}

function toActionError(error: any): JocClubActionError {
  if (error?.code === 'ACTION_REJECTED' || error?.code === 4001) {
    return new JocClubActionError('REJECTED', 'Request cancelled in your wallet.');
  }
  switch (decodeRevertName(error)) {
    case 'AlreadyMember':
      return new JocClubActionError('ALREADY_MEMBER', 'Already a JOC Club member.');
    case 'NotMember':
      return new JocClubActionError('NOT_MEMBER', 'Not a JOC Club member yet.');
    case 'InsufficientAmount':
      return new JocClubActionError(
        'INSUFFICIENT_AMOUNT',
        'The amount is below the minimum required to join.',
      );
    case 'SoulboundTransfer':
      return new JocClubActionError(
        'SOULBOUND_TRANSFER',
        'This membership pass cannot be transferred.',
      );
    default:
      return new JocClubActionError(
        'UNKNOWN',
        'Something went wrong talking to the JOC Club contract.',
      );
  }
}

// Broadcast so every useMemberInfo instance (dashboard, sidebar, ...) refetches
// after a membership mutation (join / addStake / leave).
const MEMBER_UPDATED_EVENT = 'joc-club:member-updated';

export function notifyMemberUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(MEMBER_UPDATED_EVENT));
  }
}

function getReadProvider(): ethers.JsonRpcProvider {
  return new ethers.JsonRpcProvider(RPC_URL);
}

function getReadContract(): ethers.Contract {
  return new ethers.Contract(CONTRACT_ADDRESS, JOCClubMembershipABI, getReadProvider());
}

function getWriteContract(): ethers.Contract {
  if (!window.ethereum) throw new Error('Wallet not connected');
  const provider = new ethers.BrowserProvider(window.ethereum as any);
  return provider
    .getSigner()
    .then((signer) => new ethers.Contract(CONTRACT_ADDRESS, JOCClubMembershipABI, signer)) as any;
}

export function useMemberInfo(address: string | undefined) {
  const [member, setMember] = useState<MemberInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchMember = useCallback(async () => {
    if (!address || !CONTRACT_ADDRESS) return;
    setLoading(true);
    try {
      const contract = getReadContract();
      const result = await contract.memberOf(address);
      setMember({
        lockedAmount: BigInt(result.lockedAmount),
        tier: Number(result.tier),
        joinedAt: Number(result.joinedAt),
      });
    } catch (e) {
      console.error('Failed to fetch member info:', e);
      setMember(null);
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    fetchMember();
    window.addEventListener(MEMBER_UPDATED_EVENT, fetchMember);
    return () => window.removeEventListener(MEMBER_UPDATED_EVENT, fetchMember);
  }, [fetchMember]);

  return {
    member,
    loading,
    refetch: fetchMember,
    tierName: member ? TIER_NAMES[member.tier] : 'None',
  };
}

export function useJocClubActions() {
  const [pending, setPending] = useState(false);

  const chainId = CHAIN_ID;
  const chainIdHex = '0x' + chainId.toString(16);
  const isLocal = RPC_URL.includes('localhost');
  const chainName = isLocal
    ? 'Hardhat Local'
    : chainId === 10081
    ? 'Japan Open Chain Testnet'
    : 'Japan Open Chain';

  const addChain = useCallback(async () => {
    await window.ethereum.request({
      method: 'wallet_addEthereumChain',
      params: [
        {
          chainId: chainIdHex,
          chainName,
          rpcUrls: [RPC_URL],
          nativeCurrency: { name: 'JOC', symbol: 'JOC', decimals: 18 },
        },
      ],
    });
  }, [chainIdHex, chainName]);

  const walletSeesContract = useCallback(async () => {
    try {
      const provider = new ethers.BrowserProvider(window.ethereum as any);
      const code = await provider.getCode(CONTRACT_ADDRESS);
      return code !== '0x' && code !== '0x0' && !!code;
    } catch {
      return false;
    }
  }, []);

  const ensureNetwork = useCallback(async () => {
    if (!window.ethereum) throw new Error('Wallet not connected');

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainIdHex }],
      });
    } catch (switchError: any) {
      if (switchError.code === 4902) {
        await addChain();
      }
    }

    // Self-heal: the wallet may have this chainId registered with a stale RPC URL
    // (e.g., after a local port migration). Verify the contract is reachable through
    // the wallet's RPC; if not, prompt the wallet to (re-)add the network with the
    // current RPC URL and check again.
    if (!(await walletSeesContract())) {
      await addChain();
      if (!(await walletSeesContract())) {
        throw new Error(
          `Wallet RPC cannot reach the JOC Club contract. ` +
            `Please update your wallet network (chainId ${chainId}) RPC URL to ${RPC_URL}.`,
        );
      }
    }
  }, [chainIdHex, chainId, addChain, walletSeesContract]);

  const getSigner = useCallback(async () => {
    if (!window.ethereum) throw new Error('Wallet not connected');
    if (!CONTRACT_ADDRESS) throw new Error('Contract address not configured');
    await ensureNetwork();
    const provider = new ethers.BrowserProvider(window.ethereum as any);
    const signer = await provider.getSigner();
    return new ethers.Contract(CONTRACT_ADDRESS, JOCClubMembershipABI, signer);
  }, [ensureNetwork]);

  const join = useCallback(
    async (amountJoc: string) => {
      setPending(true);
      try {
        const contract = await getSigner();
        const value = ethers.parseEther(amountJoc);
        // Pre-flight via eth_call so a revert (e.g. already a member) surfaces
        // decodable error data — Hardhat's eth_estimateGas can return an opaque
        // failure for custom errors, which is what sending straight to the
        // wallet would otherwise hit.
        try {
          await contract.join.staticCall({ value });
        } catch (simulationError) {
          throw toActionError(simulationError);
        }
        const tx = await contract.join({ value });
        await tx.wait();
        notifyMemberUpdated();
        return tx.hash;
      } catch (error) {
        throw error instanceof JocClubActionError ? error : toActionError(error);
      } finally {
        setPending(false);
      }
    },
    [getSigner],
  );

  const addStake = useCallback(
    async (amountJoc: string) => {
      setPending(true);
      try {
        const contract = await getSigner();
        const value = ethers.parseEther(amountJoc);
        try {
          await contract.addStake.staticCall({ value });
        } catch (simulationError) {
          throw toActionError(simulationError);
        }
        const tx = await contract.addStake({ value });
        await tx.wait();
        notifyMemberUpdated();
        return tx.hash;
      } catch (error) {
        throw error instanceof JocClubActionError ? error : toActionError(error);
      } finally {
        setPending(false);
      }
    },
    [getSigner],
  );

  const leave = useCallback(async () => {
    setPending(true);
    try {
      const contract = await getSigner();
      try {
        await contract.leave.staticCall();
      } catch (simulationError) {
        throw toActionError(simulationError);
      }
      const tx = await contract.leave();
      await tx.wait();
      notifyMemberUpdated();
      return tx.hash;
    } catch (error) {
      throw error instanceof JocClubActionError ? error : toActionError(error);
    } finally {
      setPending(false);
    }
  }, [getSigner]);

  return { join, addStake, leave, pending };
}
